import React from 'react';
import { Municipality, getFeesForYear, getYearOverYearChange, filterFees, DEFAULT_ENABLED_CATEGORIES } from '@/lib/data';

interface MiniBarsWidgetProps {
  municipalities: Municipality[];
  year: number;
  limit?: number;
  enabledCategories?: string[];
  dark?: boolean;
}

const getChangeClass = (change: number, dark?: boolean) => {
  if (change > 0) return dark ? 'text-amber-400' : 'text-amber-600';
  if (change < 0) return dark ? 'text-slate-400' : 'text-slate-600';
  return dark ? 'text-slate-500' : 'text-warmGray';
};

export const MiniBarsWidget: React.FC<MiniBarsWidgetProps> = ({
  municipalities,
  year,
  limit = 4,
  enabledCategories = DEFAULT_ENABLED_CATEGORIES,
  dark,
}) => {
  const data = municipalities
    .map(m => ({
      m,
      total: filterFees(getFeesForYear(m, year), enabledCategories).reduce((s, f) => s + f.amount, 0),
      change: getYearOverYearChange(m, year, enabledCategories),
    }))
    .filter(x => x.total > 0)
    .sort((a, b) => b.total - a.total)
    .slice(0, limit);

  const maxTotal = data.length > 0 ? data[0].total : 1;

  return (
    <div className="space-y-3">
      {data.map(({ m, total, change }) => (
        <div key={m.id} className="flex items-center gap-3">
          <span className={`w-20 truncate text-xs shrink-0 ${dark ? 'text-slate-400' : 'text-deepCharcoal'}`}>
            {m.name}
          </span>
          <div className={`flex-1 h-1.5 rounded-full overflow-hidden ${dark ? 'bg-slate-700' : 'bg-gray-100'}`}>
            <div
              className={`h-full rounded-full ${dark ? 'bg-slate-500' : 'bg-nordicBlue/60'}`}
              style={{ width: `${(total / maxTotal) * 100}%` }}
            />
          </div>
          <div className="flex items-center gap-1.5 w-24 justify-end shrink-0">
            {change !== null && (
              <span className={`text-[10px] tabular-nums ${getChangeClass(change, dark)}`}>
                {change > 0 ? '+' : ''}{change.toFixed(1)}%
              </span>
            )}
            <span className={`text-xs tabular-nums ${dark ? 'text-slate-400' : 'text-nordicBlue'}`}>
              {total.toLocaleString('nb-NO')} kr
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};
